import { Injectable, inject } from '@angular/core';

import { LoginResponseDto } from '../models/environment.model';
import {
    AuthApiProfile,
    AuthApiRole,
    AuthApiUser,
    ExtractCurrentUserOptions,
    IdentityMenuModuleDto,
    TokenUserData,
} from '../models/auth-api.model';
import { User } from '../models/user.model';
import { buildPermissionClaim } from '../utils/permission-action.utils';
import { getInitialsFromParts } from '../utils/text.utils';
import { LoggerService } from './logger.service';

@Injectable({ providedIn: 'root' })
export class UserContextService {
    private readonly logger = inject(LoggerService);

    isRbiAdmin(currentUser: User | null, token: string): boolean {
        const role = currentUser?.role?.toLowerCase()?.trim() || '';
        if (role === 'rbi admin' || role === 'rbi_admin') {
            return true;
        }

        return this.isSuperUser(currentUser, token);
    }

    isSuperUser(currentUser: User | null, token: string): boolean {
        if (currentUser?.isSuperUser) {
            return true;
        }

        if (!token) {
            return false;
        }

        return this.isSuperUserFromPayload(this.parseJwtPayload(token));
    }

    isSuperUserFromPayload(tokenPayload: Record<string, unknown> | null | undefined): boolean {
        return String(tokenPayload?.['isSuperUser']).toLowerCase() === 'true';
    }

    getProfileNamesFromToken(token: string): string[] {
        if (!token) {
            return [];
        }
        const parsed = this.parseJwtPayload(token);
        return this.extractProfileNamesFromPayload(parsed);
    }

    parseJwtPayload(token: string): Record<string, unknown> | null {
        try {
            const parts = token.split('.');
            if (parts.length < 2) {
                return null;
            }

            const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
            const normalizedPayload = payload.padEnd(Math.ceil(payload.length / 4) * 4, '=');
            const decoded = atob(normalizedPayload);
            const bytes = Uint8Array.from(decoded, (char) => char.charCodeAt(0));
            const decodedJson =
                typeof TextDecoder !== 'undefined'
                    ? new TextDecoder('utf-8').decode(bytes)
                    : decoded;
            return JSON.parse(decodedJson) as Record<string, unknown>;
        } catch (error) {
            this.logger.warn('UserContextService: Failed to parse JWT payload', error);
            return null;
        }
    }

    normalizeLoginResponse(response: unknown): LoginResponseDto | null {
        const source = response as Record<string, unknown> | null | undefined;
        if (!source || typeof source !== 'object') {
            return null;
        }

        const resultCandidate = source['result'];
        const dataCandidate = source['data'];

        const payload = (resultCandidate && typeof resultCandidate === 'object'
            ? resultCandidate
            : dataCandidate && typeof dataCandidate === 'object'
                ? dataCandidate
                : source) as Record<string, unknown>;

        const token = String(payload['token'] ?? payload['accessToken'] ?? '').trim();
        if (!token) {
            return null;
        }

        const refreshToken = String(payload['refreshToken'] ?? '').trim();
        const expiresAtRaw = String(payload['expiresAt'] ?? payload['expiration'] ?? '').trim();
        const expiresAt = expiresAtRaw || new Date(Date.now() + 60 * 60 * 1000).toISOString();

        return {
            token,
            refreshToken,
            expiresAt,
        };
    }

    extractCurrentUser(
        response: unknown,
        tokenPayload: Record<string, unknown>,
        options?: ExtractCurrentUserOptions,
    ): AuthApiUser | null {
        const payload = response as { result?: AuthApiUser; results?: AuthApiUser[] } | AuthApiUser | null | undefined;
        const preferEmailMatch = options?.preferEmailMatch ?? false;
        const allowSubMatch = options?.allowSubMatch ?? true;
        const fallbackToFirstResult = options?.fallbackToFirstResult ?? true;
        const tokenEmail = String(tokenPayload['email'] ?? '').toLowerCase().trim();
        const tokenSub = String(tokenPayload['sub'] ?? '').trim();

        if (payload && typeof payload === 'object' && 'result' in payload && payload.result) {
            const resultUser = payload.result;
            if (preferEmailMatch && tokenEmail) {
                const resultEmail = String(resultUser?.email ?? '').toLowerCase().trim();
                if (resultEmail && resultEmail !== tokenEmail) {
                    return null;
                }
            }

            return resultUser;
        }

        if (payload && typeof payload === 'object' && 'results' in payload) {
            const results = payload.results;
            if (!Array.isArray(results) || results.length === 0) {
                return null;
            }

            if (tokenEmail) {
                const emailMatch = results.find((user) => String(user?.email ?? '').toLowerCase() === tokenEmail);
                if (emailMatch) {
                    return emailMatch;
                }
            }

            if (allowSubMatch && tokenSub) {
                const subMatch = results.find((user) => String(user?.id ?? '') === tokenSub);
                if (subMatch) {
                    return subMatch;
                }
            }

            if (fallbackToFirstResult) {
                this.logger.warn('UserContextService: Could not match user from token claims, falling back to first result');
                return results[0];
            }

            return null;
        }

        return (payload as AuthApiUser | null) ?? null;
    }

    buildSuperUserFromToken(tokenPayload: Record<string, unknown>): AuthApiUser | null {
        const tokenUser = this.extractTokenUserData(tokenPayload);
        const email = tokenUser.email;
        if (!email) {
            return null;
        }

        const rawSub = String(tokenPayload['sub'] ?? '').trim();
        const parsedSub = Number.parseInt(rawSub, 10);
        const id = Number.isFinite(parsedSub) ? parsedSub : -1;

        return {
            id,
            email,
            firstName: tokenUser.firstName,
            lastName: tokenUser.lastName,
            empId: null,
            active: true,
            roles: [],
            profiles: [],
        };
    }

    mapApiUser(apiUser: AuthApiUser, token: string, menuPermissions: string[] = []): User {
        const parsedToken = this.parseJwtPayload(token);
        const tokenUser = this.extractTokenUserData(parsedToken);
        const isSuperUser = this.isSuperUserFromPayload(parsedToken);
        const firstName = isSuperUser
            ? tokenUser.firstName || String(apiUser.firstName ?? '').trim()
            : String(apiUser.firstName ?? '').trim() || tokenUser.firstName;
        const lastName = isSuperUser
            ? tokenUser.lastName || String(apiUser.lastName ?? '').trim()
            : String(apiUser.lastName ?? '').trim() || tokenUser.lastName;
        const fullName = `${firstName} ${lastName}`.trim();
        const tokenPermissions = Array.isArray(parsedToken?.['permission'])
            ? (parsedToken['permission'] as string[])
            : typeof parsedToken?.['permission'] === 'string'
                ? [parsedToken['permission'] as string]
                : [];
        const permissions = this.extractPermissionsFromUser(apiUser, tokenPermissions, menuPermissions);
        const roles = this.extractRoleNames(apiUser.roles);
        const profiles = this.extractProfileNames(apiUser.profiles);
        const email =
            (isSuperUser ? tokenUser.email : '') ||
            String(apiUser.email ?? '').trim().toLowerCase() ||
            tokenUser.email;

        return {
            id: String(apiUser.id),
            name: fullName || email,
            firstName,
            lastName,
            email,
            role: roles[0] ?? 'Usuario',
            roles,
            profiles,
            profileNames: tokenUser.profileNames,
            environment: tokenUser.environment,
            positionName: tokenUser.positionName,
            branchName: tokenUser.branchName,
            departmentName: tokenUser.departmentName,
            empId: apiUser.empId ?? null,
            initials: getInitialsFromParts(firstName, lastName, email.slice(0, 2).toUpperCase()),
            isActive: apiUser.active,
            isSuperUser,
            permissions,
        };
    }

    applyUserPatch(currentUser: User, patch: Partial<User>): User {
        const nextUser: User = {
            ...currentUser,
            ...patch,
        };

        if (!nextUser.name) {
            nextUser.name = `${nextUser.firstName ?? ''} ${nextUser.lastName ?? ''}`.trim() || nextUser.email;
        }

        nextUser.initials = getInitialsFromParts(
            nextUser.firstName,
            nextUser.lastName,
            nextUser.email.slice(0, 2).toUpperCase(),
        );

        return nextUser;
    }

    extractPermissionsFromMenu(modules: IdentityMenuModuleDto[]): string[] {
        const permissions = new Set<string>();

        for (const module of modules ?? []) {
            for (const section of module.sections ?? []) {
                for (const page of section.pages ?? []) {
                    for (const permission of page.permissions ?? []) {
                        const permissionIdentifier = String(permission.permissionIdentifier ?? '').trim();

                        if (permissionIdentifier) {
                            permissions.add(permissionIdentifier);
                        }

                        const claim = buildPermissionClaim(
                            module.permissionIdentifier ?? module.name,
                            section.permissionIdentifier ?? section.name,
                            page.permissionIdentifier ?? page.name,
                            permission.action,
                        );
                        if (claim) {
                            permissions.add(claim);
                        }
                    }
                }
            }
        }

        return [...permissions];
    }

    private extractRoleNames(roles: (string | AuthApiRole)[] | undefined): string[] {
        return (roles ?? [])
            .map((role) => typeof role === 'string' ? role : role?.name ?? '')
            .filter((role): role is string => !!role);
    }

    private extractProfileNames(profiles: (string | AuthApiProfile)[] | undefined): string[] {
        const normalized = (profiles ?? [])
            .map((profile) => {
                if (typeof profile === 'string') {
                    return profile;
                }

                if (profile?.profileName != null) {
                    return String(profile.profileName);
                }

                if (profile?.name != null) {
                    return String(profile.name);
                }

                return '';
            })
            .map((profile) => profile.trim())
            .filter((profile): profile is string => profile.length > 0);

        return [...new Set(normalized)];
    }

    private extractPermissionsFromUser(
        apiUser: AuthApiUser,
        fallbackPermissions: string[],
        menuPermissions: string[] = [],
    ): string[] {
        const permissions = new Set<string>();

        for (const role of apiUser.roles ?? []) {
            if (typeof role === 'string') {
                continue;
            }

            for (const rolePermission of role.rolePermissions ?? role.permissions ?? []) {
                const permissionIdentifier = rolePermission.permission?.permissionIdentifier;
                if (permissionIdentifier) {
                    permissions.add(permissionIdentifier);
                    continue;
                }

                const action = rolePermission.permission?.action;
                const page = rolePermission.permissionPage ?? rolePermission.permission?.page;
                const pageIdentifier = page?.permissionIdentifier;
                const sectionIdentifier = page?.section?.permissionIdentifier;
                const moduleIdentifier = page?.section?.module?.permissionIdentifier;

                if (action && pageIdentifier && sectionIdentifier && moduleIdentifier) {
                    permissions.add(
                        `permission:${moduleIdentifier}:${sectionIdentifier}:${pageIdentifier}:${action}`,
                    );
                    // Además el claim canónico: es el que consulta `canAccess` (menú de emergencia sin /user/menu).
                    const claim = buildPermissionClaim(moduleIdentifier, sectionIdentifier, pageIdentifier, action);
                    if (claim) {
                        permissions.add(claim);
                    }
                }
            }
        }

        for (const permission of menuPermissions) {
            permissions.add(permission);
        }

        if (permissions.size === 0) {
            for (const permission of fallbackPermissions) {
                permissions.add(permission);
            }
        }

        return [...permissions];
    }

    private extractProfileNamesFromPayload(tokenPayload: Record<string, unknown> | null): string[] {
        const raw = tokenPayload?.['profileNames'];
        if (raw == null) {
            return [];
        }

        if (Array.isArray(raw)) {
            return raw
                .map((value) => String(value ?? '').trim())
                .filter((value) => value.length > 0);
        }

        return String(raw)
            .split(',')
            .map((value) => value.trim())
            .filter((value) => value.length > 0);
    }

    private extractTokenUserData(tokenPayload: Record<string, unknown> | null): TokenUserData {
        const firstName =
            this.normalizeText(String(tokenPayload?.['firstName'] ?? '').trim()) ||
            this.normalizeText(String(tokenPayload?.['name'] ?? '').trim());

        return {
            firstName,
            lastName: this.normalizeText(String(tokenPayload?.['lastName'] ?? '').trim()),
            email: this.normalizeText(String(tokenPayload?.['email'] ?? '').trim()).toLowerCase(),
            environment: this.normalizeText(String(tokenPayload?.['environment'] ?? '').trim()),
            positionName: this.normalizeText(String(tokenPayload?.['positionName'] ?? '').trim()),
            branchName: this.normalizeText(String(tokenPayload?.['branchName'] ?? '').trim()),
            departmentName: this.normalizeText(String(tokenPayload?.['departmentName'] ?? '').trim()),
            profileNames: this.extractProfileNamesFromPayload(tokenPayload),
        };
    }

    private normalizeText(value: string): string {
        const normalized = String(value ?? '').trim();
        if (!normalized) {
            return '';
        }

        if (!/[ÃÂ]/.test(normalized)) {
            return normalized;
        }

        try {
            const repaired = decodeURIComponent(escape(normalized));
            return repaired.trim() || normalized;
        } catch {
            return normalized;
        }
    }
}

